import axios from 'axios';
import { useContext, useEffect, useState } from 'react';
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaEnvelope,
  FaShoppingBag,
  FaStar,
  FaUser
} from 'react-icons/fa';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import '../../../assets/css/admin/adminUsersManagement/customerdetailscreen.css';
import AppContext from '../../../context/AppContext';

/* ── helpers ─────────────────────────────────────────── */
const getAvatarColor = (id = '', name = '') => {
  const colors = [
    '#2C5F7C', '#4A9E8E', '#FFB800', '#DC3545', '#17A2B8',
    '#6C757D', '#28A745', '#E83E8C', '#FD7E14', '#20C997',
  ];
  const str = (id + name).toString();
  const hash = str.split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);
  return colors[Math.abs(hash) % colors.length];
};

const formatDate = (d) => {
  if (!d) return 'Not available';
  return new Date(d).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

/* ── component ───────────────────────────────────────── */
const CustomerDetailScreen = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { baseUrl, authToken } = useContext(AppContext);

  const [customer, setCustomer] = useState(state?.user || null);
  const [loading, setLoading] = useState(!state?.user);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, msg: '', type: 'success' });

  useEffect(() => {
    if (!customer && id) {
      setLoading(true);
      axios.get(`${baseUrl}/customer/${id}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      })
        .then((res) => {
          if (res.data.success) setCustomer(res.data.customer);
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [id, customer, baseUrl, authToken]);

  const showToast = (msg, type = 'success') => {
    setToast({ show: true, msg, type });
    setTimeout(() => setToast({ show: false, msg: '', type: 'success' }), 3000);
  };
 if (loading) {
    return (
      <div className="cds-loading">
        <div className="cds-spinner" />
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="cds-not-found">
        <h3>Customer not found</h3>
        <button className="cds-back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Go Back
        </button>
      </div>
    );
  }

  const isActive = customer.cus_status === 'active';
  const avatarBg = getAvatarColor(customer.id, customer.cus_username);

  return (
    <div className="cds-container">

      {toast.show && (
        <div className={`cds-toast cds-toast-${toast.type}`}>
          {toast.msg}
        </div>
      )}

      <div className="cds-topbar">
        <button className="cds-back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back
        </button>
        <h1 className="cds-page-title">Customer Details</h1>
      </div>

      <div className="cds-hero">
        <div className="cds-avatar" style={{ background: avatarBg }}>
          {customer.cus_username?.charAt(0).toUpperCase() || 'C'}
        </div>
        <div className="cds-hero-info">
          <h2 className="cds-name">
            {customer.cus_username}
            {isActive && <FaCheckCircle className="cds-verified-icon" />}
          </h2>
          <div className="cds-badges">
          <span className={`cds-status-badge cds-active`}>
           Active  {/* always Active */}
           </span>
           <span className="cds-role-badge">
            <FaUser /> Customer
            </span>
             </div>
          <p className="cds-id">User ID: #{customer.id}</p>
        </div>

      </div>

      <div className="cds-grid">

        <div className="cds-card">
          <h3 className="cds-card-title">
            <FaEnvelope className="cds-card-icon" /> Contact Information
          </h3>
          <div className="cds-field">
            <label>Email Address</label>
            <p>{customer.cus_email || '—'}</p>
          </div>
          <div className="cds-field">
            <label>Phone Number</label>
            <p>{customer.cus_phone || 'Not provided'}</p>
          </div>
          <div className="cds-field">
            <label>Location</label>
            <p>{customer.cus_location || 'Not specified'}</p>
          </div>
        </div>

        <div className="cds-card">
          <h3 className="cds-card-title">
            <FaCalendarAlt className="cds-card-icon" /> Account Information
          </h3>
          <div className="cds-field">
            <label>Account Status</label>
            <p className={isActive ? 'cds-text-success' : 'cds-text-danger'}>
              {isActive ? 'Active' : 'Inactive'}
            </p>
          </div>
          <div className="cds-field">
            <label>Role</label>
            <p>{customer.cus_role || 'Customer'}</p>
          </div>
          <div className="cds-field">
            <label>Joined Date</label>
            <p>{formatDate(customer.created_at)}</p>
          </div>
          <div className="cds-field">
            <label>Last Updated</label>
            <p>{formatDate(customer.updated_at)}</p>
          </div>
        </div>

        <div className="cds-card">
          <h3 className="cds-card-title">
            <FaShoppingBag className="cds-card-icon" /> Booking Statistics
          </h3>
          <div className="cds-stats-row">
            <div className="cds-stat">
              <span className="cds-stat-value">{customer.totalBookings || 0}</span>
              <span className="cds-stat-label">Total Bookings</span>
            </div>
            <div className="cds-stat">
              <span className="cds-stat-value">{customer.completedBookings || 0}</span>
              <span className="cds-stat-label">Completed</span>
            </div>
            <div className="cds-stat">
              <span className="cds-stat-value">{customer.cancelledBookings || 0}</span>
              <span className="cds-stat-label">Cancelled</span>
            </div>
          </div>
        </div>

        <div className="cds-card">
          <h3 className="cds-card-title">
            <FaClock className="cds-card-icon" /> Activity Summary
          </h3>
          <div className="cds-activity-row">
            <div className="cds-activity-stat">
              <span className="cds-act-value">{customer.totalReviews || 0}</span>
              <span className="cds-act-label">Reviews</span>
            </div>
            <div className="cds-activity-stat">
              <span className="cds-act-value">{customer.favorites || 0}</span>
              <span className="cds-act-label">Favorites</span>
            </div>
            <div className="cds-activity-stat">
              <span className="cds-act-value">{customer.totalSpent || 0}</span>
              <span className="cds-act-label">Total Spent (PKR)</span>
            </div>
          </div>
        </div>

        <div className="cds-card">
          <h3 className="cds-card-title">
            <FaStar className="cds-card-icon" /> Additional Info
          </h3>
          <div className="cds-field">
            <label>User Type</label>
            <p>Regular Customer</p>
          </div>
          <div className="cds-field">
            <label>Avg. Rating Given</label>
            <p>{customer.avgRating || '—'} / 5</p>
          </div>
          <div className="cds-field">
            <label>Last Active</label>
            <p>{formatDate(customer.last_active)}</p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default CustomerDetailScreen;